// Detecta cuándo hay una versión nueva desplegada.
// Los despliegues se hacen de noche, fuera de horario, así que el caso típico
// es que la pestaña quedó abierta con el JS viejo y el usuario vuelve a ella
// en la mañana sin haber interactuado todavía: ahí se puede recargar sola,
// sin aviso. Solo si el mismatch se detecta con la pestaña ya activa (alguien
// usando la app en vivo) se avisa en vez de recargar de golpe.
import { useCallback, useEffect, useRef, useState } from 'react';

const BUILD_ID = process.env.REACT_APP_BUILD_ID || null;
const POLL_INTERVAL_MS = 30 * 60 * 1000; // 30 min

async function fetchRemoteBuildId() {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.buildId || null;
  } catch {
    return null;
  }
}

export default function useVersionCheck() {
  const [hayActualizacion, setHayActualizacion] = useState(false);
  const wasHiddenRef = useRef(document.visibilityState === 'hidden');

  const recargarAhora = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    // Sin build id embebido (ej. entorno de desarrollo local) no hay nada que comparar.
    if (!BUILD_ID) return undefined;

    let cancelado = false;

    const checkVersion = async (reloadIfStale) => {
      const remoteId = await fetchRemoteBuildId();
      if (cancelado || !remoteId || remoteId === BUILD_ID) return;

      if (reloadIfStale) {
        recargarAhora();
      } else {
        setHayActualizacion(true);
      }
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        const volvioDeSegundoPlano = wasHiddenRef.current;
        wasHiddenRef.current = false;
        checkVersion(volvioDeSegundoPlano);
      } else {
        wasHiddenRef.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkVersion(false);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelado = true;
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      clearInterval(interval);
    };
  }, [recargarAhora]);

  return { hayActualizacion, recargarAhora };
}
